// Numeric parcel fields offered in the height/colour visualization pickers. Single source of truth for
// their label, the actual tile property to read, and the value range used to normalize height/colour.
//
// Ranges are the global min/max across all 38M parcels, taken from the pmtiles `tilestats` block. They
// are stable properties of the national dataset; a later refinement could read them from the fetched
// tile metadata (PmTilesRenderingService already loads it) instead of hardcoding.
//
// POG metrics carry a _min/_max pair in the tiles; here each is collapsed to its _max variant (matching
// the filters UI). `scale: "log"` tames the area fields, whose maxima are extreme outliers.

type ParcelNumericFieldScale = "linear" | "log";

// Sentinel `colorField` values selecting a POG-vs-MPZP comparison mode. None is a PARCEL_NUMERIC_FIELDS
// key — findParcelNumericField returns null for all three.
const PARCEL_COLOR_PUM_DIFF_KEY = "pum__diff";
const PARCEL_COLOR_HEIGHT_DIFF_KEY = "height__diff";
const PARCEL_COLOR_PRZEZNACZENIE_DIFF_KEY = "przeznaczenie__diff";

type ParcelNumericField = {
	key: string;
	label: string;
	unit?: string;
	tileProperty: string;
	min: number;
	max: number;
	scale: ParcelNumericFieldScale;
};

const PARCEL_NUMERIC_FIELDS: Array<ParcelNumericField> = [
	{
		key: "parcel__pole_geometryczne",
		label: "Powierzchnia geometryczna",
		unit: "m²",
		tileProperty: "parcel__pole_geometryczne",
		min: 0,
		max: 274_341_198.94,
		scale: "log",
	},
	{
		key: "parcel__pole_ewidencyjne",
		label: "Powierzchnia ewidencyjna",
		unit: "m²",
		tileProperty: "parcel__pole_ewidencyjne",
		min: 0,
		max: 2_551_200,
		scale: "log",
	},
	{ key: "pog__pum", label: "PUM", unit: "m²", tileProperty: "pog__pum", min: 0, max: 474_848.05, scale: "linear" },
	{
		key: "pog__maksWysokoscZabudowy",
		label: "Wysokość zabudowy PO",
		unit: "m",
		tileProperty: "pog__maksWysokoscZabudowy_max",
		min: 0,
		max: 130,
		scale: "linear",
	},
	// Range copied from the POG field: the MPZP height only reaches the tiles from the next pipeline run,
	// so there are no tilestats for it yet. Worth re-checking against them afterwards.
	{ key: "mpzp__wysokoscMaxM", label: "Wysokość zabudowy MPZP", unit: "m", tileProperty: "mpzp__wysokoscMaxM_max", min: 0, max: 130, scale: "linear" },
	{
		key: "pog__maksNadziemnaIntensywnoscZabudowy",
		label: "Intensywność zabudowy",
		tileProperty: "pog__maksNadziemnaIntensywnoscZabudowy_max",
		min: 0,
		max: 16,
		scale: "linear",
	},
	{
		key: "pog__maksUdzialPowierzchniZabudowy",
		label: "Powierzchnia zabudowy",
		unit: "%",
		tileProperty: "pog__maksUdzialPowierzchniZabudowy_max",
		min: 0,
		max: 100,
		scale: "linear",
	},
	{
		key: "pog__minUdzialPowierzchniBiologicznieCzynnej",
		label: "Powierzchnia biologicznie czynna",
		unit: "%",
		tileProperty: "pog__minUdzialPowierzchniBiologicznieCzynnej_max",
		min: 0,
		max: 100,
		scale: "linear",
	},
];

function findParcelNumericField(key: string | null): ParcelNumericField | null {
	if (key === null) {
		return null;
	}

	return PARCEL_NUMERIC_FIELDS.find((field) => field.key === key) ?? null;
}

export {
	findParcelNumericField,
	PARCEL_COLOR_HEIGHT_DIFF_KEY,
	PARCEL_COLOR_PRZEZNACZENIE_DIFF_KEY,
	PARCEL_COLOR_PUM_DIFF_KEY,
	PARCEL_NUMERIC_FIELDS,
	type ParcelNumericField,
	type ParcelNumericFieldScale,
};
